import { ChevronRight, LoaderCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button, cn } from "@shared/ui";

import {
  AREAS,
  MIN_DETAILS_LENGTH,
  REASON_LABELS,
  REPORT_TEXT_LIMIT,
  ReportReason,
  ReportingArea,
} from "../../lib/reports";
import { inputClassName } from "../Field";
import { radioRowClassName } from "../Radio";
import StepBody from "../StepBody";
import StepFooter from "../StepFooter";
import StepHeading from "../StepHeading";
import { useReport } from "./ReportProvider";

type ReportFormProps = {
  area: ReportingArea;
};

const ReportForm = ({ area }: ReportFormProps) => {
  const reporting = useReport();
  // The row picked for Something else, shown pressed during the short pause.
  const [choosing, setChoosing] = useState<ReportReason>();
  const textarea = useRef<HTMLTextAreaElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  // Timer for the short pause after choosing Something else.
  const pause = useRef<number | undefined>(undefined);
  const state = reporting?.reports[area];
  // A send from the list stays on the list; a failed one shows the error layout.
  const showDetails =
    state?.status === "writing" ||
    state?.status === "failed" ||
    (state?.status === "sending" && state.reason === "other");

  useEffect(() => {
    heading.current?.focus();
  }, []);

  useEffect(() => () => window.clearTimeout(pause.current), []);

  useEffect(() => {
    if (showDetails) textarea.current?.focus();
  }, [showDetails]);

  if (!reporting || !state) return null;

  // Like the exit screen, wait for Continue instead of leaving on a timer.
  if (state.status === "sent") {
    return (
      <>
        <StepBody centered className="items-center gap-4 text-center">
          <div
            style={{ backgroundImage: 'url("./icons/manual-required.svg")' }}
            className="h-32 w-full flex-none bg-contain bg-center bg-no-repeat"
            aria-hidden
          />
          <StepHeading
            align="center"
            title="Feedback sent"
            description="Thank you for sharing feedback. It will help us improve the experience for everyone."
          />
        </StepBody>
        <StepFooter>
          <Button onClick={reporting.close} name="report-confirm-continue">
            Continue
          </Button>
        </StepFooter>
      </>
    );
  }

  const inputId = `report-${area}`;
  const isOther = state.reason === "other";
  // Sending a predefined reason from the list (the text-box flow sends from the text box).
  const isSending = state.status === "sending" && !showDetails;

  return (
    <>
      <StepBody className={isOther && showDetails ? "grow" : undefined}>
        <h1
          ref={heading}
          tabIndex={-1}
          className="text-title text-ink text-balance focus:outline-none"
        >
          Report a problem
        </h1>
        <section
          className={cn(
            "flex w-full flex-col gap-4 text-left",
            isOther && showDetails && "grow"
          )}
          aria-label="Feedback"
          aria-busy={state.status === "sending"}
        >
          {showDetails ? (
            isOther ? (
              <div className="relative flex min-h-24 basis-1/2 shrink-0 flex-col pt-2">
                <label
                  htmlFor={inputId}
                  className="absolute left-2 top-0 max-w-[calc(100%-16px)] truncate bg-surface px-1 text-small text-ink-secondary"
                >
                  {REASON_LABELS.other}
                </label>
                <textarea
                  ref={textarea}
                  id={inputId}
                  rows={4}
                  required
                  maxLength={REPORT_TEXT_LIMIT}
                  value={state.details}
                  disabled={state.status === "sending"}
                  placeholder={`Add details (at least ${MIN_DETAILS_LENGTH} characters)`}
                  aria-describedby={
                    state.status === "failed"
                      ? `${inputId}-count ${inputId}-error`
                      : `${inputId}-count`
                  }
                  onChange={(event) =>
                    reporting.setDetails(area, event.target.value)
                  }
                  className={cn(
                    inputClassName,
                    "block h-auto min-h-24 grow resize-none py-3 disabled:opacity-60"
                  )}
                />
                <span
                  id={`${inputId}-count`}
                  className="absolute -bottom-2 right-2 bg-surface px-1 text-small text-ink-secondary"
                >
                  {state.details.length}/{REPORT_TEXT_LIMIT}
                </span>
              </div>
            ) : (
              <p className="flex w-full items-center rounded-lg border border-ink bg-surface-pressed p-4 text-body-lg font-semibold text-ink">
                {state.reason && REASON_LABELS[state.reason]}
              </p>
            )
          ) : (
            <div
              role="group"
              aria-label="Feedback problem"
              className="flex flex-col gap-3"
            >
              {AREAS[area].reasons.map((reason) => {
                const chosen = isSending && state.reason === reason;
                // Other rows are hidden, not removed, so nothing below moves.
                const hidden = isSending && !chosen;
                return (
                  <button
                    key={reason}
                    type="button"
                    aria-pressed={(choosing ?? state.reason) === reason}
                    aria-hidden={hidden || undefined}
                    disabled={choosing !== undefined || isSending}
                    onClick={() => {
                      if (reason === "other") {
                        setChoosing(reason);
                        // The chosen row stays visible briefly before the text box replaces the list.
                        pause.current = window.setTimeout(() => {
                          reporting.chooseReason(area, reason);
                          setChoosing(undefined);
                        }, 300);
                      } else {
                        reporting.chooseReason(area, reason);
                      }
                    }}
                    className={cn(
                      radioRowClassName,
                      "justify-between text-body-lg font-semibold aria-pressed:border-ink aria-pressed:bg-surface-pressed",
                      hidden && "invisible",
                      chosen && "opacity-60"
                    )}
                  >
                    {REASON_LABELS[reason]}
                    {chosen ? (
                      <LoaderCircle
                        aria-hidden
                        className="size-5 shrink-0 text-ink-secondary motion-safe:animate-spin"
                        strokeWidth={1.75}
                      />
                    ) : (
                      <ChevronRight
                        aria-hidden
                        className="size-5 shrink-0 text-ink-secondary"
                        strokeWidth={1.75}
                      />
                    )}
                  </button>
                );
              })}
              <span className="sr-only" aria-live="polite">
                {isSending ? "Submitting…" : ""}
              </span>
            </div>
          )}
          {state.status === "failed" && (
            <p
              id={`${inputId}-error`}
              role="alert"
              className="text-small text-danger-ink"
            >
              Oops, we couldn’t send your feedback. Please try submitting again.
            </p>
          )}
        </section>
      </StepBody>
      {showDetails && (
        <StepFooter>
          <Button
            type="button"
            disabled={
              state.status === "sending" ||
              (isOther && state.details.trim().length < MIN_DETAILS_LENGTH)
            }
            onClick={() => void reporting.submit(area)}
          >
            {state.status === "sending" ? "Submitting…" : "Submit"}
          </Button>
        </StepFooter>
      )}
    </>
  );
};

export default ReportForm;
